import React, { useEffect } from "react";
import { observer } from "mobx-react";
import { Link, useHistory } from "react-router-dom";
import { signalsData } from "Stores/signalsData";
import { toasterState } from "Components/common/toaster";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import SignalIcon from "./SignalIcon";
import SignalsFeed, { FeedError } from "./SignalsFeed";
import SignalsFirstVisit from "./SignalsFirstVisit";
import { familyOfType, sellerProfileFor } from "./signalMeta";
import { watchesCompetitors } from "./custom/customSignalMeta";
import { Info } from "./tracker/SignalTrackerPage";
import { openAutomate } from "./SignalAutomateDrawer";
import "./tracker/signalTracker.scss";

const agentsOf = row => (row && row.agents) || [];
const isPaused = row => ((row && row.signal) || {}).status === "PAUSED";
const trackedLink = id => `/signals/tracked/${encodeURIComponent(id)}`;

/** Live feed: every new move from what you track, newest first. `focusSignal` narrows it to one tracked signal. */
export const LiveFeed = observer(({ focusSignal, onFocus, onBrowse, onTrack, onOpenSettings }) => {
  const rows = signalsData.saved;
  const noRows = !rows.length;
  // With nothing tracked the feed can still hold free signals and moves from a signal that was stopped: look first.
  useEffect(() => {
    if (noRows && !signalsData.savedError) signalsData.fetchFeed({ view: "all", page: 0, size: 25, reuseWithinMs: 10 * 1000 });
  }, [noRows]);

  if (noRows && signalsData.savedError) {
    return <FeedError onRetry={() => Promise.all([signalsData.fetchCatalog(), signalsData.fetchSaved()])} />;
  }
  if (noRows && !signalsData.feedItems.length) {
    if (signalsData.feedError) return <FeedError onRetry={() => signalsData.fetchFeed({ view: "all", page: 0, size: 25 })} />;
    if (signalsData.feedLoading || !signalsData.feedLoadedAt) return <div className="sg-skel" />;
    return <SignalsFirstVisit onTrack={onTrack} onBrowse={onBrowse} />;
  }

  const focused = rows.find(r => r.signal && r.signal.id === focusSignal);
  return (
    <>
      {focused && (
        <div className="sg-focusbar">
          Showing <b>{focused.displayName}</b>
          <Link className="sg-link" to={trackedLink(focused.signal.id)}>Open signal</Link>
          <button className="sg-link" onClick={() => onFocus("")}>Show all</button>
        </div>
      )}
      <SignalsFeed savedSignalId={focused ? focused.signal.id : ""} onOpenSettings={onOpenSettings} />
    </>
  );
});

/** My signals: every signal you track, one row each, opening its own page. */
const MySignals = ({ onBrowse, onEdit }) => {
  const history = useHistory();
  const rows = signalsData.saved;
  if (!rows.length && signalsData.savedError) {
    return <FeedError onRetry={() => Promise.all([signalsData.fetchCatalog(), signalsData.fetchSaved()])} />;
  }
  const credits = rows.reduce((n, r) => n + (Number(r && r.creditsUsedTotal) || 0), 0);
  const live = rows.filter(r => !isPaused(r)).length;

  const agentNote = row => {
    const n = agentsOf(row).length;
    return n ? ` The ${n === 1 ? "agent" : `${n} agents`} using it will stop running.` : "";
  };
  const askThen = (content, actionBtnText, run) => {
    confirmationPopupState.setPopupValues({ content, actionBtnText, needCancelBtn: true, callback: run });
    confirmationPopupState.setShowPopup(true);
  };
  const togglePause = row => {
    const paused = isPaused(row);
    const run = async () => {
      if (await signalsData.setPaused(row.signal.id, !paused)) {
        toasterState.setToastMsg(paused ? "Tracking resumed" : "Tracking paused", "success");
      }
    };
    if (paused) run();
    else askThen(`Pause “${row.displayName}”? It stops checking and uses no credits until you resume it.${agentNote(row)}`, "Pause", run);
  };
  const remove = row =>
    askThen(`Stop tracking “${row.displayName}”? Past moves stay in your feed.${agentNote(row)}`, "Stop tracking", async () => {
      if (await signalsData.deleteSignal(row.signal.id)) toasterState.setToastMsg("Stopped tracking", "success");
    });

  return (
    <div>
      <p className="sg-mine__sum">
        {live} live{rows.length > live ? ` · ${rows.length - live} paused` : ""} · {credits.toLocaleString()} signal credits used so far
      </p>
      <div className="st-table sg-mine">
        <div className="st-row st-row--head sg-mine__row">
          <span>Signal</span>
          <span className="sg-mine__th">New <Info text="Moves from this signal you haven't opened yet in the Live feed." /></span>
          <span className="sg-mine__th">Agent <Info text="Don't wait to act. An agent picks up each new move the day it lands: finds the right people, drafts the email and adds them to a list or sequence. Anything that leaves Salesgear waits for your OK." /></span>
          <span>Credits used</span><span />
        </div>
        {rows.map(row => {
          const s = row.signal || {};
          const paused = isPaused(row);
          const fam = familyOfType(signalsData.catalog, s.signalType);
          const fresh = signalsData.unreadFor(s.id);
          const agents = agentsOf(row);
          const watching = [row.scopeLabel && `Watching ${row.scopeLabel}`, sellerProfileFor(row)].filter(Boolean).join(" · ");
          return (
            <div key={s.id} className={`st-row sg-mine__row ${paused ? "is-paused" : ""}`}>
              <Link className="st-cell sg-mine__name" to={trackedLink(s.id)}>
                <span className={`sg-tchip__ic ${fam.key}`}><SignalIcon id={s.signalType} size={14} /></span>
                <span className="st-stack">
                  <span className="st-name">{row.displayName || row.signalLabel || "Signal"}</span>
                  <span className="st-sub">{paused ? (s.pauseReason === "credit_cap" ? "Paused at its credit cap" : "Paused") : watching || "Live"}</span>
                </span>
              </Link>
              <span className="sg-mine__cell" data-label="New">{fresh > 0 ? <button className="sg-link" onClick={() => history.push(`/signals?tab=feed&signal=${encodeURIComponent(s.id)}`)}>{fresh} new</button> : <span className="st-sub">None</span>}</span>
              <span className="st-sub sg-mine__cell">
                {agents.length ? <Link className="sg-link" to={`/agents/${agents[0].id}`}>{agents[0].name || "Agent"}{agents[0].status === "ACTIVE" ? " · Live" : ""}</Link>
                  : watchesCompetitors(row) ? "Not for competitors"
                    : <button className="sg-btn sg-btn--ghost sg-btn--small" onClick={() => openAutomate(row)}><SignalIcon id="robot" size={14} />Automate</button>}
              </span>
              <span className="st-sub" data-label="Credits used">{(Number(row.creditsUsedTotal) || 0).toLocaleString()}</span>
              <span className="st-act sg-mine__acts">
                <button className="sg-link sg-link--icon" onClick={() => onEdit(row)}><i className="material-icons-outlined" aria-hidden="true">edit</i>Edit</button>
                <button className="sg-link sg-link--icon" onClick={() => togglePause(row)}>
                  <i className="material-icons-outlined" aria-hidden="true">{paused ? "play_arrow" : "pause"}</i>{paused ? "Resume" : "Pause"}
                </button>
                <button className="sg-link sg-link--quiet sg-link--icon" aria-label="Stop tracking" onClick={() => remove(row)}><i className="material-icons-outlined" aria-hidden="true">block</i>Stop</button>
              </span>
            </div>
          );
        })}
        <div className="st-row sg-mine__row">
          <Link className="st-cell sg-mine__name" to="/signals/competitors">
            <span className="sg-tchip__ic cmp"><SignalIcon id="competitors" size={14} /></span>
            <span className="st-stack">
              <span className="st-name">Your competitors</span>
              <span className="st-sub">Free · no signal credits</span>
            </span>
          </Link>
          <span /><span /><span /><span />
        </div>
      </div>
      <button className="sg-tchip sg-tchip--add sg-mine__add" onClick={onBrowse}><SignalIcon id="plus" size={14} /> Track a signal</button>
    </div>
  );
};

export default observer(MySignals);
